/**
 * FERP Portal - Primitive Form Modal
 *
 * Generic modal wrapper for creating and editing primitives.
 * Dynamically renders the appropriate form based on primitive type.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only (NO MUI)
 * - Backend ID generation via usePrimitiveMutations
 * - Form validation per primitive type
 * - Life-critical confirmation dialogs
 *
 * NOTE: Form implementations are pending. This is a placeholder
 * until primitive-specific forms are fully implemented.
 */

import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Title,
  Text,
  Button,
  Alert,
  Badge,
  Center,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconX,
  IconTool,
} from '@tabler/icons-react';

import type { PrimitiveType, AnyPrimitive } from './hooks/usePrimitiveData';
import { PRIMITIVE_PHASES } from './hooks/usePrimitiveData';

// ============================================================================
// TYPES
// ============================================================================

export interface PrimitiveFormModalProps {
  opened: boolean;
  onClose: () => void;
  primitiveType: PrimitiveType;
  mode: 'create' | 'edit';
  initialData?: AnyPrimitive;
  onSuccess?: (primitive: AnyPrimitive) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PRIMITIVE_LABELS: Record<PrimitiveType, string> = {
  // DESIGN
  workflow: 'Workflow',
  template: 'Template',
  dependency: 'Dependency',
  policy: 'Policy',
  role: 'Role',
  // CONFIGURE
  team: 'Team',
  actor: 'Actor',
  validation: 'Validation',
  contract: 'Contract',
  constraint: 'Constraint',
  // EXECUTE
  instance: 'Instance',
  task: 'Task',
  approval: 'Approval',
  document: 'Document',
  queue: 'Queue',
  // MONITOR
  event: 'Event',
  notification: 'Notification',
  trace: 'Trace',
  checkpoint: 'Checkpoint',
  transaction: 'Transaction',
  // OPTIMIZE
  audit: 'Audit',
  analytics: 'Analytics',
  schedule: 'Schedule',
  resource: 'Resource',
  integration: 'Integration',
};

const PHASE_COLORS: Record<string, string> = {
  DESIGN: 'blue',
  CONFIGURE: 'grape',
  EXECUTE: 'cyan',
  MONITOR: 'orange',
  OPTIMIZE: 'pink',
};

const LIFE_CRITICAL_PRIMITIVES: PrimitiveType[] = [
  'workflow', 'policy', 'actor', 'task', 'approval',
  'audit', 'contract',
];

// ============================================================================
// COMPONENT
// ============================================================================

export function PrimitiveFormModal({
  opened,
  onClose,
  primitiveType,
  mode,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initialData,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSuccess,
}: PrimitiveFormModalProps) {
  const [showLifeCriticalWarning, setShowLifeCriticalWarning] = useState(false);

  const phase = PRIMITIVE_PHASES[primitiveType] ?? 'DESIGN';
  const phaseColor = PHASE_COLORS[phase];
  const label = PRIMITIVE_LABELS[primitiveType];
  const isLifeCritical = LIFE_CRITICAL_PRIMITIVES.includes(primitiveType);

  // Reset state when modal closes
  useEffect(() => {
    if (!opened) {
      setShowLifeCriticalWarning(false);
    }
  }, [opened]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={null}
      size="lg"
      padding="md"
      styles={{
        header: { display: 'none' },
        body: { padding: 0 },
      }}
    >
      <Stack gap={0}>
        {/* Header */}
        <Group justify="space-between" p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
          <div>
            <Group gap="xs" mb={4}>
              <Badge color={phaseColor} variant="light" size="sm">
                {phase}
              </Badge>
              {isLifeCritical && (
                <Badge color="red" variant="light" size="sm" leftSection={<IconAlertTriangle size={10} />}>
                  Life-Critical
                </Badge>
              )}
            </Group>
            <Title order={3}>
              {mode === 'create' ? `Create ${label}` : `Edit ${label}`}
            </Title>
          </div>
          <Button variant="subtle" color="gray" onClick={onClose}>
            <IconX size={18} />
          </Button>
        </Group>

        {/* Placeholder Content */}
        <Center py="xl" px="md">
          <Stack ta="center" gap="md">
            <ThemeIcon size="xl" variant="light" color="gray">
              <IconTool size={32} />
            </ThemeIcon>
            <Title order={4} c="dimmed">Coming Soon</Title>
            <Text c="dimmed" ta="center" maw={400}>
              The {label.toLowerCase()} form is under development.
              <br />
              Primitive-specific forms will be available in a future release.
            </Text>
            <Alert color="blue" variant="light" title="Development Note">
              <Text size="sm">
                Each primitive requires a dedicated form with specific validation rules,
                field types, and safety constraints. These forms are being developed
                to match the backend GraphQL mutations.
              </Text>
            </Alert>
            <Button variant="light" onClick={onClose}>
              Close
            </Button>
          </Stack>
        </Center>
      </Stack>
    </Modal>
  );
}

export default PrimitiveFormModal;
